import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { CardsForm } from './CardsForm'
import { Welcome } from './Welcome'

import { Modal } from '@/components/common/Modal'
import { FormNavigation } from '@/components/form-controls'
import { useDisclosure } from '@/components/nextui'
import { useToast } from '@/context/ToastContext'
import { useAuthHook } from '@/hooks'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useAuthStore } from '@/store/authStore'
import { MessageLevel } from '@/types'
import { ErrorHandlingStrategy } from '@/types/api'
import { AuthorizationType, Authorization } from '@/types/authorizationForm'
import { AuthorizationFormData } from '@/types/forms/authorizationForm'
import { FormsProps } from '@/types/forms/formsProps'
import { useFormProgress } from '@/utils'

// eslint-disable-next-line max-lines-per-function
export const WelcomeModule = ({
    onNext,
    onPrevious
}: FormsProps): JSX.Element => {
    const { control, handleSubmit, watch, reset } =
        useForm<AuthorizationFormData>()
    const { t } = useTranslation()
    const { idAspirant } = useAuthHook()
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const { showToast } = useToast()
    const { inscriptionStatusId } = useAuthStore()
    const { isOpen, onClose, onOpen } = useDisclosure()

    // Verificar si el aspirante está admitido/matriculado (inscriptionStatusId === 3, 4 o 6)
    const isBlocked =
        inscriptionStatusId === 3 ||
        inscriptionStatusId === 4 ||
        inscriptionStatusId === 6

    const handleBlockedClick = () => {
        if (isBlocked) {
            onOpen()
        }
    }

    const progress = useFormProgress<AuthorizationFormData>({
        watch,
        excludedFields: [
            'id',
            'updatedBy',
            'aspirantId',
            'acceptProgramTerms',
            'acceptTerms',
            'economicActivity',
            'secondEconomicActivity',
            'isMinor'
        ]
    })
    const watchData = watch()

    console.log('this is the progress', progress)
    console.log('this is the watch', watchData)
    const [isDisabled, setIsDisabled] = useState(false)

    const text = 'authorization'

    const onSubmit: SubmitHandler<AuthorizationFormData> = async (data) => {
        if (isDisabled) {
            onNext()

            return
        }

        try {
            const response = await aspirantsApi.postAuthorizationAspiranByScope(
                {
                    idAspirant: idAspirant!,
                    updatedBy: idAspirant!,
                    authorizations: [
                        {
                            authorizationTypeId:
                                AuthorizationType.EMAIL_NOTIFICATIONS,
                            isAccepted: data.acceptEmailNotifications || false,
                            valueText: ''
                        },
                        {
                            authorizationTypeId:
                                AuthorizationType.DATA_PROCESSING,
                            isAccepted: data.acceptDataProcessing || false,
                            valueText: ''
                        },
                        {
                            authorizationTypeId:
                                AuthorizationType.SENSITIVE_DATA,
                            isAccepted: data.acceptSensitiveData || false,
                            valueText: ''
                        }
                    ]
                }
            )

            if (response.status === 200) {
                showToast({
                    title: t(`${text}.title`),
                    message: t('forms.ui.save'),
                    type: 'success'
                })
                setIsDisabled(true)
                onNext()
            } else {
                showToast({
                    title: t(`${text}.error.title`),
                    message: t(`${text}.error.message`),
                    type: 'error'
                })
            }
        } catch (error) {
            showToast({
                title: t(`${text}.error.title`),
                message: t(`${text}.error.message`),
                type: 'error'
            })
        }
    }

    useEffect(() => {
        const loadAuthorizationData = async (): Promise<void> => {
            if (!idAspirant) {
                return
            }
            //const response = await aspirantsApi.getAuthorizationForm(idAspirant)

            const responseScope =
                await aspirantsApi.getAuthorizationAspirantScope(idAspirant)

            console.log('this is the responseScope', responseScope)

            /* if (response.status === 200) {
                reset(response.data)
                if (
                    Boolean(response.data.acceptDataProcessing) ||
                    Boolean(response.data.acceptSensitiveData) ||
                    Boolean(response.data.acceptEmailNotifications)
                ) {
                    setIsDisabled(true)
                }
            }*/

            if (responseScope.status === 200 && responseScope.data) {
                // Transform the array format to match form structure
                const formData = {
                    acceptEmailNotifications: responseScope.data.some(
                        (auth: Authorization) =>
                            auth.authorizationType ===
                                AuthorizationType.EMAIL_NOTIFICATIONS &&
                            auth.isAccepted
                    ),
                    acceptDataProcessing: responseScope.data.some(
                        (auth: Authorization) =>
                            auth.authorizationType ===
                                AuthorizationType.DATA_PROCESSING &&
                            auth.isAccepted
                    ),
                    acceptSensitiveData: responseScope.data.some(
                        (auth: Authorization) =>
                            auth.authorizationType ===
                                AuthorizationType.SENSITIVE_DATA &&
                            auth.isAccepted
                    )
                }

                reset(formData)

                // Check if any authorizations are accepted to disable the form
                const hasAcceptedAuthorizations = responseScope.data.some(
                    (auth: Authorization) => auth.isAccepted
                )

                if (hasAcceptedAuthorizations) {
                    setIsDisabled(true)
                }
            }
        }

        loadAuthorizationData()
    }, [idAspirant, reset])

    return (
        <div className={`relative ${isBlocked ? 'opacity-60' : ''}`}>
            {isBlocked && (
                <>
                    <div className="absolute inset-0 z-10 bg-gray-200 bg-opacity-50 cursor-not-allowed" />
                    <div
                        className="absolute inset-0 z-20 bg-transparent cursor-pointer"
                        onClick={handleBlockedClick}
                    />
                    <div className="absolute top-2 right-2 z-30 bg-yellow-100 border border-yellow-400 text-yellow-800 px-2 py-1 rounded text-xs">
                        🔒 Campos bloqueados - Click para más información
                    </div>
                </>
            )}
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                text="inscriptionDataChange"
            />
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="w-full"
            >
                <Welcome />
                <CardsForm
                    control={control}
                    text={text}
                    isDisabled={isDisabled}
                />

                <FormNavigation
                    onSubmit={handleSubmit(onSubmit)}
                    onPrevious={onPrevious}
                    progress={progress}
                />
            </form>
        </div>
    )
}
